import type { PreloadConfig, PreloadResult, PreloadTask } from "./google-drive-preloader.types";
import { logger } from "@/lib/logger";

export async function checkAccountAvailability(
  accessToken: string,
  signal: AbortSignal | undefined
): Promise<{ primaryAvailable: boolean; secondaryAvailable: boolean }> {
  try {
    const response = await fetch("/api/unifiedtime/getDrive/status", {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      signal,
    });

    if (!response.ok) {
      return { primaryAvailable: false, secondaryAvailable: false };
    }

    const status = await response.json();
    return {
      primaryAvailable: status.primary?.connected && status.primary?.hasDriveAccess,
      secondaryAvailable: status.secondary?.connected && status.secondary?.hasDriveAccess,
    };
  } catch (error) {
    logger.warn("🚀 Google Drive Preloader: Error checking account availability", { error });
    return { primaryAvailable: false, secondaryAvailable: false };
  }
}

function buildBrowseUrl(task: PreloadTask): string {
  const queryParams = new URLSearchParams();
  if (task.cacheKey.parentId) queryParams.set("parentId", task.cacheKey.parentId);
  if (task.cacheKey.driveId) queryParams.set("driveId", task.cacheKey.driveId);
  if (task.cacheKey.action) queryParams.set("action", task.cacheKey.action);
  if (task.cacheKey.email) queryParams.set("email", task.cacheKey.email);
  if (task.cacheKey.isSecondAccount) queryParams.set("isSecondAccount", "true");
  return `/api/unifiedtime/getDrive/browse?${queryParams.toString()}`;
}

export async function executePreloadTask(
  task: PreloadTask,
  accessToken: string,
  config: PreloadConfig,
  parentSignal: AbortSignal | undefined
): Promise<PreloadResult> {
  const startTime = Date.now();

  try {
    logger.info(`🚀 Google Drive Preloader: Loading ${task.description}...`);

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), config.timeoutPerRequest);

    if (parentSignal?.aborted) {
      throw new Error("Preload cancelled");
    }

    const response = await fetch(buildBrowseUrl(task), {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();
    const duration = Date.now() - startTime;

    logger.info(`🚀 Google Drive Preloader: ✅ ${task.description} (${duration}ms)`);

    return {
      success: true,
      cacheKey: task.cacheKey,
      data,
      duration,
    };
  } catch (error: any) {
    const duration = Date.now() - startTime;
    logger.info(`🚀 Google Drive Preloader: ❌ ${task.description} (${duration}ms):`, {
      error: error.message,
    });

    return {
      success: false,
      cacheKey: task.cacheKey,
      error: error.message,
      duration,
    };
  }
}
